import React, { lazy, Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';

// project imports
import AppShell from '../app/AppShell';
import RequireRole from '../app/RequireRole';
import { PageLoading, NotFound } from '../app/fallbacks';

// Roles: 5 order admin, 8 edit admin, 7 payment admin, 6 super admin.
const ORDER = [5, 8];
const PAYMENT = [7];
const SUPER = [6];
const ANY = undefined; // any signed-in role

// Orders and punching
const OrderDashboard = lazy(() => import('../views/dashboard/OrderDashboard'));
const OrdersTable = lazy(() => import('../views/table/OrdersTable'));
const CancelledOrdersTable = lazy(() => import('../views/table/CancelledOrdersTable'));
const CreateOrder = lazy(() => import('../views/table/CreateOrder'));
const EditOrder = lazy(() => import('../views/table/EditOrder'));
const BulkOrderUpdate = lazy(() => import('../views/table/BulkOrderUpdate'));
const PrfConnect = lazy(() => import('../views/table/PrfConnect'));
const InserOrderImportCsv = lazy(() => import('../views/table/CreateOrderCsv')); //insert order via csv import
const EditCancelledOrder = lazy(() => import('../views/table/EditCancelledOrder'));
const TempOrderTable = lazy(() => import('../views/table/tempOrder/TempOrderTable'));
const ViewTempOrderDetails = lazy(() => import('../views/table/tempOrder/ViewTempOrderDetails'));
const Order360 = lazy(() => import('../views/orders/Order360'));

// Payment admin
const PaymentDashboard = lazy(() => import('../views/dashboard/PaymentDashboard'));
const PayoutRuns = lazy(() => import('../views/table/paymentAdmin/runs/PayoutRuns'));
const RunBuilder = lazy(() => import('../views/table/paymentAdmin/runs/RunBuilder'));
const RunDetail = lazy(() => import('../views/table/paymentAdmin/runs/RunDetail'));
const RedeemRequests = lazy(() => import('../views/table/paymentAdmin/redeemRequests/RedeemRequests'));
const PaymentTable = lazy(() => import('../views/table/paymentAdmin/PaymentTable'));
const EditPaymentPage = lazy(() => import('../views/table/paymentAdmin/paymentEditPage'));
const PayoutTable = lazy(() => import('../views/table/paymentAdmin/payout/PayoutTable'));
const CreatePayout = lazy(() => import('../views/table/paymentAdmin/payout/CreatePayout'));
const PaymentLedger = lazy(() => import('../views/table/paymentAdmin/ledger/Ledger'));
const DealCoins = lazy(() => import('../views/table/paymentAdmin/coins/DealCoins'));
// One Holds page for /payment-holds (role 7, paymentAdmin hold endpoints) and
// /order-holds (roles 5/8, orderAdmin hold endpoints); it picks them by role.
const PaymentHolds = lazy(() => import('../views/table/paymentAdmin/holds/Holds'));
const OrderHolds = PaymentHolds;
const TdsRegister = lazy(() => import('../views/table/paymentAdmin/tds/TdsRegister'));
const TdsCertificates = lazy(() => import('../views/table/paymentAdmin/tds/TdsCertificates'));
const PaymentTransactionsTable = lazy(() => import('../views/table/paymentAdmin/paymentTransactions/PaymentTransactionsTable'));
const EditOrderPaymentAdmin = lazy(() => import('../views/table/paymentAdmin/EditOrder'));

// Super admin (payment details are shared with the payment admin)
const PaymentDetailsTable = lazy(() => import('../views/table/superAdmin/PaymentDetailsTable'));
const EditPaymentDetails = lazy(() => import('../views/table/superAdmin/EditPaymentDetails'));
const CreditTable = lazy(() => import('../views/table/superAdmin/CreditTable'));
const EditPaymentAmt = lazy(() => import('../views/table/superAdmin/UpdatePaymentAd'));

// Legacy CRM pages (unchanged)
const ExportTable = lazy(() => import('../views/table/ExportTable'));
const Users = lazy(() => import('../views/table/Users'));
const EditUser = lazy(() => import('../views/Forms/EditUser'));
const Lead_form = lazy(() => import('../views/Forms/Lead_form'));
const EditLead = lazy(() => import('../views/Forms/EditLead'));
const AssignLead = lazy(() => import('../views/Forms/LeadAssign'));
const callSummaryTable = lazy(() => import('../views/table/CallSummaryTable'));

// Every route: path, the roles that may open it, the page. Paths match exactly.
const ROUTES = [
    { path: '/order-dashboard', roles: ORDER, component: OrderDashboard },
    { path: '/prfConnect', roles: ORDER, component: PrfConnect },
    { path: '/create-order', roles: ORDER, component: CreateOrder },
    { path: '/insertOrder', roles: ORDER, component: InserOrderImportCsv },
    { path: '/tempOrder', roles: ORDER, component: TempOrderTable },
    { path: '/view-temp-order-details', roles: ORDER, component: ViewTempOrderDetails },
    { path: '/orders-table', roles: [5, 6, 7, 8], component: OrdersTable },
    { path: '/edit-order', roles: ORDER, component: EditOrder },
    { path: '/bulk-orderUpdate', roles: ORDER, component: BulkOrderUpdate },
    { path: '/cancelled-orders', roles: ORDER, component: CancelledOrdersTable },
    { path: '/edit-cancelled-order', roles: ORDER, component: EditCancelledOrder },
    { path: '/order-holds', roles: ORDER, component: OrderHolds },
    { path: '/order-360', roles: [5, 7, 8], component: Order360 },

    { path: '/payment-dashboard', roles: PAYMENT, component: PaymentDashboard },
    { path: '/payout-runs', roles: PAYMENT, component: PayoutRuns },
    { path: '/payout-runs/new', roles: PAYMENT, component: RunBuilder },
    { path: '/payout-run', roles: PAYMENT, component: RunDetail },
    { path: '/redeem-requests', roles: PAYMENT, component: RedeemRequests },
    { path: '/payment-table', roles: PAYMENT, component: PaymentTable },
    { path: '/payment-edit', roles: PAYMENT, component: EditPaymentPage },
    { path: '/payout', roles: PAYMENT, component: PayoutTable },
    { path: '/create-payout', roles: PAYMENT, component: CreatePayout },
    { path: '/payment-ledger', roles: PAYMENT, component: PaymentLedger },
    { path: '/dealcoins', roles: PAYMENT, component: DealCoins },
    { path: '/payment-holds', roles: PAYMENT, component: PaymentHolds },
    { path: '/tds-register', roles: PAYMENT, component: TdsRegister },
    { path: '/tds-certificates', roles: PAYMENT, component: TdsCertificates },
    { path: '/payment-transactions', roles: PAYMENT, component: PaymentTransactionsTable },
    { path: '/edit-order-payment-admin', roles: PAYMENT, component: EditOrderPaymentAdmin },

    { path: '/paymentDetails', roles: [6, 7], component: PaymentDetailsTable },
    { path: '/edit-paymentDetails', roles: [6, 7], component: EditPaymentDetails },
    { path: '/credit', roles: SUPER, component: CreditTable },
    { path: '/update-payment', roles: SUPER, component: EditPaymentAmt },

    { path: '/export-table', roles: ANY, component: ExportTable },
    { path: '/assign-lead', roles: ANY, component: AssignLead },
    { path: '/users', roles: ANY, component: Users },
    { path: '/edituser', roles: ANY, component: EditUser },
    { path: '/leadform', roles: ANY, component: Lead_form },
    { path: '/editlead', roles: ANY, component: EditLead },
    { path: '/call-summary', roles: ANY, component: callSummaryTable }
];

//-----------------------|| MAIN ROUTING ||-----------------------//

// Everything outside /pages renders inside the app shell (signed-in users
// only). Pages load lazily; the shell stays up while they load.
const MainRoutes = () => (
    <RequireRole>
        <AppShell>
            <Suspense fallback={<PageLoading />}>
                <Switch>
                    {ROUTES.map(({ path, roles, component: Component }) => (
                        <Route
                            key={path}
                            exact
                            path={path}
                            render={(props) => (
                                <RequireRole roles={roles}>
                                    <Component {...props} />
                                </RequireRole>
                            )}
                        />
                    ))}
                    <Route component={NotFound} />
                </Switch>
            </Suspense>
        </AppShell>
    </RequireRole>
);

export default MainRoutes;
